import React, { memo, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Typography, useMediaQuery } from '@mui/material';
import theme from '@src/styles/theme';
// import percentIcon from '@src/assets/icons/stroke-discount.png';
import { map, max, min } from 'lodash';
import { delimitNumber } from '@src/utils/number';
import pluralize from '@src/utils/pluralize';
import Tooltip from '@src/components/Tooltip';
import { checkVietNam } from '@src/utils/checkCountry';
import { useSelector } from 'react-redux';
import Slider from '@mui/material/Slider';
import { COLOR } from '@src/styles/color';
import CustomTypography from '@src/new-components/Typography';
import { StyledBuyMoreCharacters } from './index.style';

const BuyExtracharacters = memo(
  ({
    // calculateCharacterPurchase,
    currentNumberCharacters,
    onChangeNumberCharacters,
    packageCode,
    packages,
  }) => {
    const { t, i18n } = useTranslation();
    const { language } = i18n;
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const { package: packageState } = useSelector((state) => state.order);
    const { volumeDiscount, creditPurchaseOptions } = packageState || {};
    const { discountRatePerStep, creditsPerDiscountStep, maxDiscountRate } =
      volumeDiscount || {};

    const getMaxDiscountMilestone = useMemo(() => {
      if (
        !discountRatePerStep ||
        !creditsPerDiscountStep ||
        !maxDiscountRate ||
        !creditPurchaseOptions?.length
      ) {
        return null;
      }

      const foundMilestone = creditPurchaseOptions.find((milestone) => {
        const steps = Math.floor(milestone / creditsPerDiscountStep);
        const discountRate = Math.min(
          steps * discountRatePerStep,
          maxDiscountRate,
        );
        return discountRate >= maxDiscountRate;
      });

      return foundMilestone || null;
    }, [
      discountRatePerStep,
      creditsPerDiscountStep,
      maxDiscountRate,
      creditPurchaseOptions,
    ]);

    const isVietNam = checkVietNam();
    const muiSliderMarkNumber = useMemo(() => {
      if (!getMaxDiscountMilestone || !creditPurchaseOptions?.length) {
        return null;
      }

      const milestoneIndex = creditPurchaseOptions.findIndex(
        (value) => value === getMaxDiscountMilestone,
      );

      return milestoneIndex;
    }, [getMaxDiscountMilestone, creditPurchaseOptions]);
    const marks = useMemo(
      () =>
        map(packageState?.creditPurchaseOptions || [], (value) => ({ value })),
      [packageState],
    );

    const maxNumberCharacter = useMemo(
      () => max(packageState?.creditPurchaseOptions || []),
      [packageState],
    );

    const minNumberCharacter = useMemo(
      () => min(packageState?.creditPurchaseOptions || []) || 0,
      [packageState],
    );

    const ValueLabelComponent = (props) => {
      const { children, value } = props;
      return (
        <Tooltip
          enterTouchDelay={0}
          placement="top"
          arrow
          title={value?.toLocaleString(language)}
        >
          {children}
        </Tooltip>
      );
    };

    const packageTopUpPackageCode = useMemo(
      () => packages?.find((pkg) => pkg?.topUpPackageCode === packageCode),
      [packages, packageCode],
    );

    const renderTextDiscount = useMemo(() => {
      const hasDiscount =
        creditsPerDiscountStep > 0 &&
        discountRatePerStep > 0 &&
        maxDiscountRate > 0;
      if (!hasDiscount) return null;

      if (currentNumberCharacters < creditsPerDiscountStep) {
        const numberCharacters =
          creditsPerDiscountStep - currentNumberCharacters;
        const discountRatePercentage = Math.floor(discountRatePerStep * 100);
        return (
          <CustomTypography
            variant="body-12-regular"
            style={{ color: COLOR.label2['64'] }}
          >
            {t('addXMoreCharactersToUnlockYourDiscount', {
              numberCharacters: numberCharacters.toLocaleString(language),
              discountRate: discountRatePercentage,
            })}
          </CustomTypography>
        );
      }

      const currentDiscountRate =
        Math.floor(currentNumberCharacters / creditsPerDiscountStep) *
        discountRatePerStep;
      const discountRate = Math.min(currentDiscountRate, maxDiscountRate);
      const discountRatePercentage = Math.floor(discountRate * 100);
      return (
        <CustomTypography
          variant="body-12-regular"
          style={{ color: COLOR.label2['64'] }}
        >
          {t('discountApplied', {
            discountRate: discountRatePercentage,
          })}
        </CustomTypography>
      );
    }, [
      creditsPerDiscountStep,
      currentNumberCharacters,
      discountRatePerStep,
      language,
    ]);

    return (
      <StyledBuyMoreCharacters>
        <div className="head">
          <span className="title">{t('buyMoreCharacters')}</span>
          {packageTopUpPackageCode && (
            <Typography
              className="product-name"
              component="span"
              dangerouslySetInnerHTML={{
                __html: t('packageNameByLevel', {
                  name: packageTopUpPackageCode?.name?.[language],
                }),
              }}
            />
          )}
        </div>
        <div className="price-per-number-character">
          *
          {isVietNam ? (
            <span className="discount-unit-price-small underline">đ</span>
          ) : (
            <span className="discount-unit-price-small">$</span>
          )}
          {isVietNam
            ? packageState?.vndPricePerUnit?.toLocaleString(language)
            : packageState?.usdPricePerUnit?.toLocaleString(language)}
          /{packageState?.creditsPerUnit?.toLocaleString(language)}{' '}
          {pluralize(packageState?.creditsPerUnit, t('credit'), language)}
        </div>
        <div className="characters-slider">
          <div className="number-price">
            <span className="title-number-price">{t('characterNumber')}</span>
            <div className="number-characters">
              <span className="number-character">
                {t('remainChars', {
                  numberCharacters: delimitNumber(
                    currentNumberCharacters,
                    language,
                  ),
                })}
              </span>
              {/* {!!calculateCharacterPurchase?.discountRate && (
                <div className="tag-discount">
                  <img
                    src={percentIcon}
                    className="percent-icon"
                    alt="percentIcon"
                  />
                  {t('percentDiscount', {
                    percentDiscount: Math.floor(
                      calculateCharacterPurchase?.discountRate * 100,
                    ),
                  })}
                </div>
              )} */}
            </div>
          </div>
          <div className="info-character">
            {t('numberCharactersWillAdded', {
              numberCharacters:
                currentNumberCharacters.toLocaleString(language),
            })}
          </div>
        </div>
        <div
          data-id="slider-character-amount"
          data-character-amount={currentNumberCharacters}
          className="slider"
        >
          <Slider
            className="slider-mui"
            sx={{
              height: 12,
              '& .MuiSlider-track': {
                backgroundColor: 'gray',
                height: 12,
                color: '#007AFF',
              },
              '& .MuiSlider-rail': {
                backgroundColor: '#ccc',
                height: 12,
              },
              '& .MuiSlider-thumb': {
                width: 24,
                height: 24,
                color: '#fff',
                background: '#fff',
                zIndex: 200,
                boxShadow: '0px 3px 8px 0px #0000003D',
              },
              '& .MuiSlider-thumb:before': {
                width: 12,
                height: 12,
                background: '#007AFF',
                zIndex: 201,
              },
              '& .MuiSlider-mark': {
                backgroundColor: 'transparent',
              },
              [`& .MuiSlider-mark[data-index="${muiSliderMarkNumber}"]`]:
                muiSliderMarkNumber !== null &&
                  getMaxDiscountMilestone &&
                  currentNumberCharacters !== getMaxDiscountMilestone && {
                    width: 12,
                    height: 12,
                    borderRadius: '50%',
                    backgroundColor: '#fff',
                    marginLeft: '-2px',
                  },
              '& .MuiSlider-thumb.Mui-focusVisible': {
                boxShadow: 'none',
              },
            }}
            components={{
              ValueLabel: ValueLabelComponent,
            }}
            disableSwap={!!isMobile}
            step={null}
            marks={marks}
            min={minNumberCharacter}
            max={maxNumberCharacter}
            value={currentNumberCharacters}
            onChange={(e) => {
              onChangeNumberCharacters(e.target.value);
            }}
            valueLabelDisplay="auto"
            getAriaValueText={() => null}
          />
        </div>
        {renderTextDiscount}
      </StyledBuyMoreCharacters>
    );
  },
);

export default BuyExtracharacters;
